import { AlertTriangle, Loader2, RotateCcw } from "lucide-react";

import { Button } from "@/components/ui";
import type { AttributeReviewMappingRow } from "@/hooks/useAttributeReview.types";
import type { ApprovalSyncFieldError } from "@/lib/attributeReview/parseApprovalSyncFieldErrors";

const MAX_FIELDS = 5;
const MAX_VALUES = 5;

type Props = {
  fieldErrors: ApprovalSyncFieldError[];
  message: string | null;
  rows: AttributeReviewMappingRow[];
  canResume: boolean;
  isResuming: boolean;
  isSaving: boolean;
  onFix: (fieldId: string) => void;
  onResume: () => void;
};

export function ApprovalSyncFailedBanner({
  fieldErrors,
  message,
  rows,
  canResume,
  isResuming,
  isSaving,
  onFix,
  onResume,
}: Props) {
  const hasFieldErrors = fieldErrors.length > 0;
  const fix = () => {
    const fieldId =
      fieldErrors.find((fieldError) =>
        rows.some((row) => row.id === fieldError.fieldId),
      )?.fieldId ?? fieldErrors[0].fieldId;
    onFix(fieldId);
    setTimeout(() => {
      document
        .getElementById(`attribute-review-row-${fieldId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  return (
    <div className="mb-4 flex flex-col gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-900 dark:border-red-900/70 dark:bg-red-950/30 dark:text-red-100 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-300" />
        <div>
          <p className="text-sm font-semibold">Approval sync failed</p>
          <p className="mt-1 text-sm text-red-800 dark:text-red-200">
            {hasFieldErrors
              ? (message ??
                "Some values could not be saved. Fix or reject them, then save again.")
              : canResume
                ? "The last approval sync failed. Resume it to continue from where it stopped — your approved mappings are kept."
                : "The last approval sync failed. Restart the mapping to recompute the pending rows and return this product type to review."}
          </p>
          {hasFieldErrors && (
            <ul className="mt-2 space-y-1 text-sm text-red-800 dark:text-red-200">
              {fieldErrors.slice(0, MAX_FIELDS).map((fieldError) => {
                const row = rows.find((r) => r.id === fieldError.fieldId);
                const shown = fieldError.missingValues.slice(0, MAX_VALUES);
                const extra = fieldError.missingValues.length - shown.length;
                return (
                  <li key={fieldError.fieldId}>
                    <span className="font-medium">
                      {`${row?.sourceAttribute ?? fieldError.fieldId}:`}
                    </span>{" "}
                    {shown.join(", ")}
                    {extra > 0 ? ` +${extra} more` : ""}
                  </li>
                );
              })}
              {fieldErrors.length > MAX_FIELDS && (
                <li>+{fieldErrors.length - MAX_FIELDS} more field(s)</li>
              )}
            </ul>
          )}
        </div>
      </div>
      <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
        {hasFieldErrors && (
          <Button
            type="button"
            onClick={fix}
            disabled={isSaving}
            className="shrink-0 bg-red-600 text-white hover:bg-red-700"
          >
            Fix these values
          </Button>
        )}
        {canResume && !hasFieldErrors && (
          <Button
            type="button"
            onClick={onResume}
            disabled={isResuming}
            className="shrink-0 bg-red-600 text-white hover:bg-red-700"
          >
            {isResuming ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4" />
            )}
            {isResuming ? "Resuming..." : "Resume from failure"}
          </Button>
        )}
      </div>
    </div>
  );
}
